import React from 'react';
import {
    Button as AriaButton,
    Dialog,
    DialogTrigger,
    Radio,
    RadioGroup,
    type RadioGroupProps,
    type RadioProps,
    composeRenderProps,
} from 'react-aria-components';

import { classnames } from '@/utils/classnames.ts';

import Popover from './Popover.tsx';

// A button (e.g. an avatar showing the current color) that opens a popover of color swatches.
// Usage: <ColorSwatchPicker trigger={…} aria-label="…"><ColorSwatchPicker.Swatch value="red" hue={25} /></ColorSwatchPicker>
export interface ColorSwatchPickerProps extends Omit<RadioGroupProps, 'children' | 'className'> {
    trigger: React.ReactNode;
    children: React.ReactNode;
    className?: string;
}

const ColorSwatchPickerRoot = ({ trigger, children, className, ...props }: ColorSwatchPickerProps) => (
    <DialogTrigger>
        <AriaButton
            aria-label={props['aria-label']}
            className={classnames(
                'shrink-0 cursor-default rounded-xl outline-none data-[focus-visible]:ring-2 data-[focus-visible]:ring-primary data-[focus-visible]:ring-offset-2',
                className
            )}
        >
            {trigger}
        </AriaButton>
        <Popover placement="bottom start" className="p-2">
            <Dialog aria-label={props['aria-label']} className="outline-none">
                <RadioGroup {...props} className="grid grid-cols-6 gap-1">
                    {children}
                </RadioGroup>
            </Dialog>
        </Popover>
    </DialogTrigger>
);

export interface ColorSwatchProps extends Omit<RadioProps, 'children'> {
    // oklch hue (0–359); without one the swatch is grey, e.g. for a "no color" option.
    hue?: number;
}

// Ported from jobwunder's color scheme picker.
const ColorSwatch = ({ hue, className, ...props }: ColorSwatchProps) => (
    <Radio
        {...props}
        className={composeRenderProps(className, (className) =>
            classnames(
                'group flex size-9 cursor-default items-center justify-center rounded-full outline-none transition-colors',
                'data-[hovered]:bg-surface-hover data-[selected]:bg-surface-hover data-[focus-visible]:ring-2 data-[focus-visible]:ring-primary',
                className
            )
        )}
    >
        <span
            style={hue === undefined ? undefined : ({ '--swatch-hue': hue } as React.CSSProperties)}
            className={classnames(
                'flex size-6 items-center justify-center rounded-full border',
                hue === undefined
                    ? 'border-border bg-surface-muted'
                    : 'border-[oklch(50%_0.17_var(--swatch-hue))] bg-[oklch(65%_0.17_var(--swatch-hue))]'
            )}
        >
            <span
                className={classnames(
                    'size-2 rounded-full opacity-0 shadow-sm group-data-[selected]:opacity-100',
                    hue === undefined ? 'bg-text-muted' : 'bg-white'
                )}
            />
        </span>
    </Radio>
);

export default Object.assign(ColorSwatchPickerRoot, {
    Swatch: ColorSwatch,
});
